import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useState, useEffect } from 'react'
import { Link } from 'react-router'

interface Property {
  id: string
  name: string
  type: string
  image: string
  href: string
  uuid: string
}

const properties: Property[] = [
  {
    id: '1',
    name: 'Seven Oaks',
    type: '7 Units of 4 Bedroom Terrace with BQ at Jahi District, Abuja',
    image: '/oaks_cover.png',
    href: '#',
    uuid: 'hE0JEkx6DKwb7UOQYavk',
  },
  {
    id: '2',
    name: 'The Loft',
    type: '3 Units of 4-Bedroom Terrace Triplex and 3 Units 3 Bedroom',
    image: '/loft_cover.png',
    href: '#',
    uuid: 'RrLVR47WAg7ogg6CPJI7',
  },
  {
    id: '3',
    name: 'The Glen',
    type: '12 Units of 3-Bedroom Apartment with a BQ in Jahi District Abuja',
    image: '/glen_cover_2.png',
    href: '',
    uuid: 'prR4HF2kgFl0DSGz2oLQ ',
  },
  {
    id: '4',
    name: 'Alsbury Residence',
    type: '8 Units of 6-Bedroom Terrace Triplex with a BQ in Durumi Abuja',
    image: '/alsbury_cover.png',
    href: '#',
    uuid: 'oy3Fa1vQo64MaRAXULlu',
  },
  {
    id: '5',
    name: 'The Plaza',
    type: '45 Units of Shops, Office and Bistro in Wuse 2 District Abuja',
    image: '/plaza_cover.png',
    href: '#',
    uuid: 'RjJ0DrZVcIve0dJuPwVl',
  },
]

export default function PropertyShowcase() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // Number of items to show based on screen size
  const getVisibleItems = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1024) return 4 // lg
      if (window.innerWidth >= 768) return 2 // md
      return 1 // sm
    }
    return 4 // default for SSR
  }

  const [visibleItems, setVisibleItems] = useState(getVisibleItems())

  useEffect(() => {
    const handleResize = () => {
      setVisibleItems(getVisibleItems())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const rotateItems = (direction: 'next' | 'prev') => {
    if (isAnimating) return

    setIsAnimating(true)
    setCurrentIndex((prevIndex) => {
      let newIndex
      if (direction === 'next') {
        newIndex = (prevIndex + 1) % properties.length
      } else {
        newIndex = prevIndex - 1
        if (newIndex < 0) newIndex = properties.length - 1
      }
      return newIndex
    })

    setTimeout(() => setIsAnimating(false), 300) // Match transition duration
  }

  const getVisibleProperties = () => {
    const visibleProps = []
    for (let i = 0; i < visibleItems; i++) {
      const index = (currentIndex + i) % properties.length
      visibleProps.push(properties[index])
    }
    return visibleProps
  }

  const goToSlide = (index: number) => {
    if (isAnimating) return
    setCurrentIndex(index)
  }

  return (
    <section className="relative py-16 overflow-hidden">
      <div className="container mx-auto px-16 sm:px-4">
        {/* Navigation Arrows */}
        <button
          className="absolute left-2 sm:left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-lg hover:bg-white transition-all duration-300 disabled:opacity-50"
          aria-label="Previous property"
          onClick={() => rotateItems('prev')}
          disabled={isAnimating}
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
        <button
          className="absolute right-2 sm:right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-white/80 p-2 shadow-lg hover:bg-white transition-all duration-300 disabled:opacity-50"
          aria-label="Next property"
          onClick={() => rotateItems('next')}
          disabled={isAnimating}
        >
          <ChevronRight className="h-6 w-6" />
        </button>

        {/* Properties Grid */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          {getVisibleProperties().map((property) => (
            <div
              key={property.id}
              className="group relative aspect-[4/3] md:aspect-[3/4] overflow-hidden rounded-lg transform transition-all duration-300"
            >
              <img
                src={property.image}
                alt={property.name}
                className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent">
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white">
                    {property.name}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200">
                    {property.type}
                  </p>
                  <Link
                    to={`/projects/${property.uuid}`}
                    className="mt-4 inline-flex items-center text-white hover:underline"
                  >
                    View Project
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Dots Navigation */}
        <div className="mt-8 flex justify-center gap-2">
          {properties.map((_, index) => (
            <button
              key={index}
              className={`h-2 w-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-gray-800 w-4'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  )
}
